import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Calendar as CalendarComponent } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock,
  Bell,
  CheckCircle,
  PartyPopper,
  CreditCard,
  Mail,
  FileText,
  Hammer,
  Edit,
  Trash2,
  AlertCircle,
  MapPin,
  Users,
  Eye
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Link as RouterLink } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const Calendario = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventos, setEventos] = useState([]);
  const [recordatorios, setRecordatorios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecordatorio, setEditingRecordatorio] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    tipo: "evento",
    titulo: "",
    descripcion: "",
    fecha: format(new Date(), "yyyy-MM-dd"),
    hora: "",
    cliente_id: "",
    evento_id: ""
  });

  const tiposRecordatorio = [
    { value: "pago", label: "Pago", icon: CreditCard, color: "bg-amber-500" },
    { value: "diseno", label: "Diseño", icon: Mail, color: "bg-blue-500" },
    { value: "entrega", label: "Entrega", icon: CheckCircle, color: "bg-emerald-500" },
    { value: "cotizacion", label: "Cotización", icon: FileText, color: "bg-purple-500" },
    { value: "evento", label: "Evento", icon: PartyPopper, color: "bg-[#C89B7B]" },
    { value: "montaje", label: "Montaje", icon: Hammer, color: "bg-stone-500" },
    { value: "saldo", label: "Saldo", icon: CreditCard, color: "bg-red-500" },
  ];

  // Colores por estatus de evento (8 estados oficiales)
  const estatusColor = {
    nuevo: "bg-stone-400",
    cotizado: "bg-blue-500",
    contratado: "bg-orange-500",
    confirmado: "bg-emerald-500",
    en_produccion: "bg-purple-500",
    en_evento: "bg-red-500",
    finalizado: "bg-emerald-900",
    cancelado: "bg-stone-700",
    // legacy fallback
    planeacion: "bg-stone-400",
    montaje: "bg-purple-500",
    en_curso: "bg-red-500",
  };
  const estatusLabel = {
    nuevo: "Nuevo", cotizado: "Cotizado", contratado: "Contratado",
    confirmado: "Confirmado", en_produccion: "En producción", en_evento: "En evento",
    finalizado: "Finalizado", cancelado: "Cancelado",
    planeacion: "Nuevo", montaje: "En producción", en_curso: "En evento",
  };

  const [eventoDetalle, setEventoDetalle] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [eventosRes, recordatoriosRes, clientesRes] = await Promise.all([
        axios.get(`${API}/eventos`),
        axios.get(`${API}/recordatorios`),
        axios.get(`${API}/clientes`)
      ]);
      
      setEventos(eventosRes.data);
      setRecordatorios(recordatoriosRes.data);
      setClientes(clientesRes.data);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      tipo: "evento",
      titulo: "",
      descripcion: "",
      fecha: format(selectedDate, "yyyy-MM-dd"),
      hora: "",
      cliente_id: "",
      evento_id: ""
    });
    setEditingRecordatorio(null);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecordatorio(rec);
    setFormData({
      tipo: rec.tipo || "evento",
      titulo: rec.titulo || "",
      descripcion: rec.descripcion || "",
      fecha: rec.fecha || format(new Date(), "yyyy-MM-dd"),
      hora: rec.hora || "",
      cliente_id: rec.cliente_id || "",
      evento_id: rec.evento_id || ""
    });
    setDialogOpen(true);
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.cliente_id === clienteId);
    if (cliente) {
      setFormData(prev => ({
        ...prev,
        cliente_id: clienteId,
        // Auto-fill fecha con la fecha del evento del cliente
        fecha: cliente.fecha_evento || prev.fecha,
        titulo: prev.titulo || `${prev.tipo === 'evento' ? 'Evento' : 'Recordatorio'}: ${cliente.nombre_festejado}`
      }));
    } else {
      setFormData(prev => ({ ...prev, cliente_id: clienteId }));
    }
  };

  const handleEventoChange = (eventoId) => {
    const evento = eventos.find(e => e.evento_id === eventoId);
    if (evento) {
      setFormData(prev => ({
        ...prev,
        evento_id: eventoId,
        cliente_id: evento.cliente_id,
        // Auto-fill fecha con la fecha del evento
        fecha: evento.fecha || prev.fecha,
        titulo: prev.titulo || `${prev.tipo === 'evento' ? 'Evento' : 'Recordatorio'}: ${evento.nombre}`
      }));
    } else {
      setFormData(prev => ({ ...prev, evento_id: eventoId }));
    }
  };

  const handleSaveRecordatorio = async () => {
    if (!formData.titulo || !formData.fecha) {
      toast.error("Título y fecha son requeridos");
      return;
    }

    try {
      if (editingRecordatorio) {
        await axios.put(`${API}/recordatorios/${editingRecordatorio.recordatorio_id}`, formData);
        toast.success("Recordatorio actualizado");
      } else {
        await axios.post(`${API}/recordatorios`, formData);
        toast.success("Recordatorio creado");
      }
      resetForm();
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar el recordatorio");
    }
  };

  const handleToggleRecordatorio = async (recordatorioId, completado) => {
    try {
      await axios.put(`${API}/recordatorios/${recordatorioId}`, { completado: !completado });
      await fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDeleteRecordatorio = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/recordatorios/${deleteId}`);
      toast.success("Recordatorio eliminado");
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  // Get items for selected date
  const { eventosDelDia, clientesDelDia, recordatoriosDelDia } = useMemo(() => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return {
      eventosDelDia: eventos.filter(e => e.fecha === dateStr),
      clientesDelDia: clientes.filter(c => c.fecha_evento === dateStr),
      recordatoriosDelDia: recordatorios.filter(r => r.fecha === dateStr)
    };
  }, [selectedDate, eventos, clientes, recordatorios]);

  // Get dates with items for calendar highlighting
  const datesWithItems = useMemo(() => {
    const dates = new Set();
    eventos.forEach(e => dates.add(e.fecha));
    clientes.forEach(c => dates.add(c.fecha_evento));
    recordatorios.forEach(r => dates.add(r.fecha));
    return dates;
  }, [eventos, clientes, recordatorios]);

  // Calendar modifiers - memoized to prevent re-renders
  const calendarModifiers = useMemo(() => ({
    hasItems: (date) => datesWithItems.has(format(date, "yyyy-MM-dd"))
  }), [datesWithItems]);

  const calendarModifiersStyles = useMemo(() => ({
    hasItems: { fontWeight: 'bold', color: '#064e3b' }
  }), []);

  // Pending reminders count
  const pendingRecordatorios = recordatorios.filter(r => !r.completado).length;

  // Próximos recordatorios (próximos 7 días)
  const today = format(new Date(), "yyyy-MM-dd");
  const proximosRecordatorios = recordatorios
    .filter(r => !r.completado && r.fecha >= today)
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .slice(0, 10);

  // Recordatorios vencidos
  const vencidos = recordatorios.filter(r => !r.completado && r.fecha < today);

  // Get cliente name for recordatorio
  const getClienteName = (clienteId) => {
    const cliente = clientes.find(c => c.cliente_id === clienteId);
    return cliente ? cliente.nombre : null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Calendario</h1>
          <p className="text-stone-600 mt-1">Eventos, entregas y recordatorios</p>
        </div>
        <Button onClick={openNewDialog} className="btn-primary" data-testid="btn-nuevo-recordatorio">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Recordatorio
        </Button>
      </div>

      {/* Alert for overdue reminders */}
      {vencidos.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div className="flex-1">
              <p className="font-medium text-red-900">
                Tienes {vencidos.length} recordatorio{vencidos.length > 1 ? 's' : ''} vencido{vencidos.length > 1 ? 's' : ''}
              </p>
              <p className="text-sm text-red-700">Revisa y actualiza tus recordatorios pendientes</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#C89B7B]/20 rounded-full flex items-center justify-center">
              <PartyPopper className="w-5 h-5 text-[#C89B7B]" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Eventos</p>
              <p className="font-display text-2xl text-stone-900">{eventos.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Invitaciones</p>
              <p className="font-display text-2xl text-stone-900">{clientes.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Pendientes</p>
              <p className="font-display text-2xl text-stone-900">{pendingRecordatorios}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar & Details */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border-stone-200 md:col-span-2">
          <CardContent className="p-6">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-sm"
              locale={es}
              modifiers={calendarModifiers}
              modifiersStyles={calendarModifiersStyles}
            />
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-emerald-900" />
              {format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })}
            </CardTitle>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                setFormData(prev => ({ ...prev, fecha: format(selectedDate, "yyyy-MM-dd") }));
                openNewDialog();
              }}
              className="border-stone-300"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Events */}
            {eventosDelDia.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Eventos</p>
                {eventosDelDia.map(evento => {
                  const colorCls = estatusColor[evento.estatus] || "bg-stone-400";
                  const cliente = clientes.find(c => c.cliente_id === evento.cliente_id);
                  return (
                    <div
                      key={evento.evento_id}
                      onClick={() => setEventoDetalle({ evento, cliente })}
                      className="p-3 border-l-4 bg-white hover:bg-stone-50 rounded-sm mb-2 cursor-pointer transition-colors"
                      style={{ borderLeftColor: undefined }}
                      data-testid={`cal-evento-${evento.evento_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-stone-900 truncate">{evento.nombre}</p>
                          <p className="text-sm text-stone-600">{evento.tipo}</p>
                          {evento.hora_inicio && (
                            <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {evento.hora_inicio} {evento.hora_fin ? `- ${evento.hora_fin}` : ""}
                            </p>
                          )}
                          {evento.ubicacion && (
                            <p className="text-xs text-stone-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {evento.ubicacion}
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded text-white ${colorCls} whitespace-nowrap`}>
                          {estatusLabel[evento.estatus] || evento.estatus}
                        </span>
                      </div>
                      {(evento.saldo > 0) && (
                        <p className="text-xs text-amber-700 mt-1">Saldo: ${evento.saldo?.toLocaleString()}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Clients with events */}
            {clientesDelDia.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Invitaciones</p>
                {clientesDelDia.map(cliente => (
                  <div key={cliente.cliente_id} className="p-3 bg-emerald-50 rounded-sm mb-2">
                    <p className="font-medium text-stone-900">{cliente.nombre_festejado}</p>
                    <p className="text-sm text-stone-600">{cliente.tipo_evento}</p>
                    <p className="text-xs text-stone-500">{cliente.nombre}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reminders */}
            {recordatoriosDelDia.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Recordatorios</p>
                {recordatoriosDelDia.map(rec => {
                  const tipoInfo = tiposRecordatorio.find(t => t.value === rec.tipo);
                  const clienteName = getClienteName(rec.cliente_id);
                  return (
                    <div 
                      key={rec.recordatorio_id} 
                      className={`p-3 rounded-sm mb-2 ${rec.completado ? 'bg-stone-100 opacity-60' : 'bg-amber-50 hover:bg-amber-100'} cursor-pointer transition-colors`}
                      onClick={() => openEditDialog(rec)}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleRecordatorio(rec.recordatorio_id, rec.completado);
                          }}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            rec.completado 
                              ? 'bg-emerald-500 border-emerald-500 text-white' 
                              : 'border-stone-300 hover:border-emerald-500'
                          }`}
                        >
                          {rec.completado && <CheckCircle className="w-3 h-3" />}
                        </button>
                        <div className="flex-1">
                          <p className={`font-medium ${rec.completado ? 'text-stone-500 line-through' : 'text-stone-900'}`}>
                            {rec.titulo}
                          </p>
                          {clienteName && (
                            <p className="text-xs text-stone-600">Cliente: {clienteName}</p>
                          )}
                          {rec.descripcion && (
                            <p className="text-sm text-stone-600">{rec.descripcion}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded-sm text-white ${tipoInfo?.color || 'bg-stone-500'}`}>
                              {tipoInfo?.label || rec.tipo}
                            </span>
                            {rec.hora && (
                              <span className="text-xs text-stone-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {rec.hora}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(rec);
                          }}
                          className="p-1 h-auto"
                        >
                          <Edit className="w-3 h-3 text-stone-400" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {eventosDelDia.length === 0 && clientesDelDia.length === 0 && recordatoriosDelDia.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>Sin eventos para este día</p>
                <Button 
                  variant="link" 
                  onClick={() => {
                    setFormData(prev => ({ ...prev, fecha: format(selectedDate, "yyyy-MM-dd") }));
                    openNewDialog();
                  }}
                  className="text-emerald-900 mt-2"
                >
                  + Agregar recordatorio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Reminders List */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Recordatorios Pendientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {proximosRecordatorios.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {proximosRecordatorios.map(rec => {
                const tipoInfo = tiposRecordatorio.find(t => t.value === rec.tipo);
                const TipoIcon = tipoInfo?.icon || Bell;
                const clienteName = getClienteName(rec.cliente_id);
                const isOverdue = rec.fecha < today;
                
                return (
                  <div 
                    key={rec.recordatorio_id}
                    onClick={() => openEditDialog(rec)}
                    className={`p-4 bg-white border rounded-sm hover:shadow-md transition-all cursor-pointer ${
                      isOverdue ? 'border-red-300 bg-red-50' : 'border-stone-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tipoInfo?.color || 'bg-stone-500'} text-white`}>
                        <TipoIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate">{rec.titulo}</p>
                        <p className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
                          {rec.fecha}
                          {rec.hora && ` a las ${rec.hora}`}
                        </p>
                        {clienteName && (
                          <p className="text-xs text-stone-500 truncate">Cliente: {clienteName}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRecordatorio(rec.recordatorio_id, rec.completado);
                            }}
                            className="text-xs text-emerald-900 hover:underline"
                          >
                            Marcar completado
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(rec);
                            }}
                            className="text-xs text-stone-500 hover:underline"
                          >
                            Editar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
              <p>No hay recordatorios pendientes</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New/Edit Reminder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingRecordatorio ? "Editar Recordatorio" : "Nuevo Recordatorio"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, tipo: v }))}
              >
                <SelectTrigger className="border-stone-300" data-testid="select-tipo-recordatorio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiposRecordatorio.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="titulo">Título *</Label>
              <Input 
                id="titulo"
                value={formData.titulo}
                onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ej: Llamar al cliente para confirmar"
                className="border-stone-300"
                data-testid="input-titulo-recordatorio"
              />
            </div>

            {/* Vincular con cliente */}
            <div className="space-y-2">
              <Label>Vincular con cliente (opcional)</Label>
              <Select 
                value={formData.cliente_id || "none"} 
                onValueChange={(v) => handleClienteChange(v === "none" ? "" : v)}
              >
                <SelectTrigger className="border-stone-300">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin vincular</SelectItem>
                  {clientes.map(cliente => (
                    <SelectItem key={cliente.cliente_id} value={cliente.cliente_id}>
                      {cliente.nombre} - {cliente.tipo_evento} ({cliente.fecha_evento})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">Al vincular, la fecha se establecerá automáticamente</p>
            </div>

            {/* Vincular con evento */}
            {eventos.length > 0 && (
              <div className="space-y-2">
                <Label>Vincular con evento (opcional)</Label>
                <Select 
                  value={formData.evento_id || "none"} 
                  onValueChange={(v) => handleEventoChange(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="border-stone-300">
                    <SelectValue placeholder="Selecciona un evento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin vincular</SelectItem>
                    {eventos.map(evento => (
                      <SelectItem key={evento.evento_id} value={evento.evento_id}>
                        {evento.nombre} ({evento.fecha})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha del recordatorio *</Label>
                <Input 
                  id="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                  className="border-stone-300"
                  data-testid="input-fecha-recordatorio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora">Hora (opcional)</Label>
                <Input 
                  id="hora"
                  type="time"
                  value={formData.hora}
                  onChange={(e) => setFormData(prev => ({ ...prev, hora: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción (opcional)</Label>
              <Textarea 
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={3}
                placeholder="Detalles adicionales..."
                className="border-stone-300"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            {editingRecordatorio && (
              <Button 
                variant="outline" 
                onClick={() => setDeleteId(editingRecordatorio.recordatorio_id)}
                className="border-red-300 text-red-600 hover:bg-red-50 mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Eliminar
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveRecordatorio} className="btn-primary" data-testid="btn-guardar-recordatorio">
              {editingRecordatorio ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar recordatorio?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteRecordatorio}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de detalle de evento */}
      <Dialog open={!!eventoDetalle} onOpenChange={() => setEventoDetalle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-evento-detalle">
          {eventoDetalle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span>{eventoDetalle.evento.nombre}</span>
                  <span className={`text-xs px-2 py-1 rounded text-white ${estatusColor[eventoDetalle.evento.estatus] || 'bg-stone-400'}`}>
                    {estatusLabel[eventoDetalle.evento.estatus] || eventoDetalle.evento.estatus}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Cliente</p>
                    <p className="font-medium">{eventoDetalle.cliente?.nombre || "—"}</p>
                  </div>
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Teléfono</p>
                    <p className="font-medium">{eventoDetalle.cliente?.telefono || eventoDetalle.cliente?.whatsapp || "—"}</p>
                  </div>
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Fecha</p>
                    <p className="font-medium">{eventoDetalle.evento.fecha}</p>
                  </div>
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Horario</p>
                    <p className="font-medium">{eventoDetalle.evento.hora_inicio || "—"} {eventoDetalle.evento.hora_fin ? `- ${eventoDetalle.evento.hora_fin}` : ""}</p>
                  </div>
                  <div className="bg-stone-50 p-2 rounded col-span-1 sm:col-span-2">
                    <p className="text-xs text-stone-500 uppercase">Ubicación</p>
                    <p className="font-medium">{eventoDetalle.evento.ubicacion || "—"}</p>
                  </div>
                  {(eventoDetalle.evento.total_invitados > 0) && (
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-xs text-stone-500 uppercase">Invitados</p>
                      <p className="font-medium flex items-center gap-1"><Users className="w-3 h-3" /> {eventoDetalle.evento.total_invitados}</p>
                    </div>
                  )}
                  {eventoDetalle.evento.menu && (
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-xs text-stone-500 uppercase">Menú</p>
                      <p className="font-medium">{eventoDetalle.evento.menu}</p>
                    </div>
                  )}
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Total</p>
                    <p className="font-medium">${(eventoDetalle.evento.total || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-stone-50 p-2 rounded">
                    <p className="text-xs text-stone-500 uppercase">Saldo</p>
                    <p className="font-medium text-amber-700">${(eventoDetalle.evento.saldo || 0).toLocaleString()}</p>
                  </div>
                </div>
                {eventoDetalle.evento.notas && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase mb-1">Notas</p>
                    <p className="text-stone-700 bg-stone-50 p-2 rounded text-sm">{eventoDetalle.evento.notas}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setEventoDetalle(null)} className="border-stone-300">Cerrar</Button>
                <RouterLink to={`/panel/eventos/${eventoDetalle.evento.evento_id}/hoja-operativa`} onClick={() => setEventoDetalle(null)}>
                  <Button variant="outline" className="border-emerald-600 text-emerald-700">
                    <FileText className="w-4 h-4 mr-1" /> Hoja Operativa
                  </Button>
                </RouterLink>
                <RouterLink to={`/panel/eventos/${eventoDetalle.evento.evento_id}`} onClick={() => setEventoDetalle(null)}>
                  <Button className="bg-emerald-900 hover:bg-emerald-800 text-white">
                    <Eye className="w-4 h-4 mr-1" /> Abrir evento
                  </Button>
                </RouterLink>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Agenda - vista de lista de proximos eventos */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-emerald-900" /> Próximos eventos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const todayStr = format(new Date(), "yyyy-MM-dd");
            const proximos = eventos
              .filter(e => e.fecha >= todayStr && e.estatus !== "cancelado" && e.estatus !== "finalizado")
              .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""))
              .slice(0, 12);
            if (proximos.length === 0) {
              return <p className="text-stone-500 text-center py-6">Sin eventos próximos</p>;
            }
            return (
              <div className="space-y-2">
                {proximos.map(evento => {
                  const cliente = clientes.find(c => c.cliente_id === evento.cliente_id);
                  return (
                    <div
                      key={evento.evento_id}
                      onClick={() => setEventoDetalle({ evento, cliente })}
                      className="flex items-center gap-3 p-3 bg-stone-50 hover:bg-stone-100 rounded cursor-pointer transition-colors"
                      data-testid={`agenda-evento-${evento.evento_id}`}
                    >
                      <div className={`w-2 h-12 rounded ${estatusColor[evento.estatus] || 'bg-stone-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate">{evento.nombre}</p>
                        <p className="text-xs text-stone-500">{evento.fecha} {evento.hora_inicio && `· ${evento.hora_inicio}`} {evento.ubicacion && `· ${evento.ubicacion}`}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded text-white ${estatusColor[evento.estatus] || 'bg-stone-400'} whitespace-nowrap hidden sm:inline-block`}>
                        {estatusLabel[evento.estatus] || evento.estatus}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default Calendario;
